import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

function pickSupportedMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4", // Safari often prefers this
  ];
  for (const type of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(type)) return type;
  }
  return ""; // let browser choose
}

export default function AudioRecorder({
  onUpload,
  uploadEndpoint = "https://youconnected-f6114d7c794c.herokuapp.com/api/upload/audio",
  uploadHeaders = {},
  filenamePrefix = "recording",
  timesliceMs = 800, // emit chunks every ~0.8s
}) {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mimeTypeRef = useRef("");

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickSupportedMime();
      mimeTypeRef.current = mimeType;

      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.start(timesliceMs); // <-- ensure we actually get dataavailable events
      setIsRecording(true);
    } catch (err) {
      console.error(err);
      Swal.fire("Permissão de microfone", "Não foi possível acessar o microfone.", "error");
    }
  };

  const stopRecording = async () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    setIsRecording(false);

    // Flush last chunk
    try { mr.requestData(); } catch {}

    const stopped = new Promise((resolve) => {
      mr.onstop = resolve;
    });

    mr.stop(); // triggers final dataavailable + stop
    // Stop mic right away (safe)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    await stopped;

    // Build blob
    const type = mimeTypeRef.current || "audio/webm";
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];

    if (blob.size === 0) {
      console.warn("Recorded blob is empty.");
      Swal.fire("Falha na gravação", "O áudio gravado saiu vazio. Tente novamente.", "warning");
      return;
    }

    setIsProcessing(true);
    try {
      const fileExt = type.includes("ogg") ? "ogg" : type.includes("mp4") ? "m4a" : "webm";
      const file = new File([blob], `${filenamePrefix}-${Date.now()}.${fileExt}`, { type });

      let finalUrl;

      if (uploadEndpoint) {
        const form = new FormData();
        form.append("file", file); // <-- ensure your backend expects "file"
        const resp = await fetch(uploadEndpoint, {
          method: "POST",
          headers: { ...uploadHeaders },
          body: form,
        });
        if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
        const data = await resp.json();
        finalUrl = data.file_url || data.url || data.path;
      }

      // Fallback to local blob if server URL missing/invalid
      if (!finalUrl) finalUrl = URL.createObjectURL(blob);

      onUpload?.(finalUrl);
    } catch (err) {
      console.error(err);
      // Fallback to local preview so you can confirm recording is OK
      const previewUrl = URL.createObjectURL(blob);
      onUpload?.(previewUrl);
      Swal.fire("Upload falhou", "Use o preview local — verifique o endpoint de upload.", "info");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className={`px-4 py-2 rounded text-white ${isRecording ? "bg-red-600" : "bg-[#389E0D]"} ${isProcessing ? "opacity-60 cursor-not-allowed" : ""}`}
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isProcessing}
      >
        {isRecording ? "Parar" : "Gravar Áudio"}
      </button>
      {isProcessing && <span className="text-sm text-gray-600">Processando…</span>}
    </div>
  );
}
